import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { CuttingService, IResponseSetup, ISetup } from '../cutting.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Setups';
const importConcurrency = 5;

type CuttingMethod = 'edge-to-edge';
type SortingStrategy = 'length-ascending' | 'length-descending';
const CUTTING_METHODS: CuttingMethod[] = ['edge-to-edge'];
const SORTING_STRATEGIES: SortingStrategy[] = ['length-ascending', 'length-descending'];

export interface ISetupExportData {
	name: string;
	description?: string;
	minLength?: number;
	maxLength?: number;
	cuttingMethod: CuttingMethod;
	kerf?: number;
	clearance?: number;
	rear?: number;
	sortingStrategy: SortingStrategy;
}

const SetupSchema: IFileDataSchema<ISetupExportData> = {
	name: sheetName,
	properties: {
		name: {
			type: 'string',
			required: true,
			fileKey: 'Name',
			width: 40,
		},
		description: {
			type: 'string',
			required: false,
			fileKey: 'Description',
			width: 30,
		},
		minLength: {
			type: 'number',
			required: false,
			fileKey: 'MinLength',
			width: 10,
		},
		maxLength: {
			type: 'number',
			required: false,
			fileKey: 'MaxLength',
			width: 10,
		},
		cuttingMethod: {
			type: 'string',
			required: true,
			fileKey: 'CuttingMethod',
			width: 20,
		},
		kerf: {
			type: 'number',
			required: false,
			fileKey: 'Kerf',
			width: 10,
		},
		clearance: {
			type: 'number',
			required: false,
			fileKey: 'Clearance',
			width: 10,
		},
		rear: {
			type: 'number',
			required: false,
			fileKey: 'Rear',
			width: 10,
		},
		sortingStrategy: {
			type: 'string',
			required: true,
			fileKey: 'SortingStrategy',
			width: 20,
		},
	},
};

interface SetupImportContext {
	setups: IResponseSetup[];
}

interface SetupImportCandidate {
	row: ISetupExportData;
	location?: string;
}

@Injectable({
	providedIn: 'root',
})
export class SetupTransferService implements ITransferService<ISetup> {
	private translationPrefix = 'masterdata.cutting.setupImportExport';

	constructor(
		private cuttingService: CuttingService,
		private excelFileTransfer: ExcelFileTransfer<ISetupExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	public exportData(setups: ISetup[]): Observable<ISetup[]> {
		const duplicateNames = findDuplicateNames(setups);
		if (duplicateNames.length > 0) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, { names: duplicateNames.join(', ') })));
		}

		const data: ISetupExportData[] = setups.map((setup) => ({
			name: setup.name,
			description: setup.description || '',
			minLength: setup.minLength,
			maxLength: setup.maxLength,
			cuttingMethod: this.cuttingService.getLastSegment(setup.cuttingMethod) as CuttingMethod,
			kerf: setup.kerf,
			clearance: setup.clearance,
			rear: setup.rear,
			sortingStrategy: this.cuttingService.getLastSegment(setup.sortingStrategy) as SortingStrategy,
		}));

		return this.excelFileTransfer.exportData(sheetName, data, SetupSchema).pipe(
			map(() => setups),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	// A blank starting point for import: the same headers exportData produces, with one example
	// row per accepted cuttingMethod/sortingStrategy combination - generated from the same arrays
	// validateRow checks against, so a new enum value automatically gets a row here too.
	public downloadTemplate(): void {
		const exampleRows: ISetupExportData[] = CUTTING_METHODS.flatMap((cuttingMethod) =>
			SORTING_STRATEGIES.map(
				(sortingStrategy) =>
					({
						name: `Example Setup (${cuttingMethod}, ${sortingStrategy})`,
						description: 'Example description',
						minLength: 1,
						maxLength: 6000,
						cuttingMethod,
						kerf: 3,
						clearance: 1,
						rear: 2,
						sortingStrategy,
					}) as ISetupExportData,
			),
		);

		this.excelFileTransfer.exportData(`${sheetName}Template`, exampleRows, SetupSchema).subscribe();
	}

	public importData(file: File | File[]): Observable<ImportResponse> {
		const ctx: SetupImportContext = { setups: [] };
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return this.cuttingService.getSetups().pipe(
					mergeMap((setups) => {
						const rosterDuplicates = findDuplicateNames(setups.items);
						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						ctx.setups = setups.items;

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ImportErrors)),
							toArray(),
							mergeMap((validRows) => (namesAreAmbiguous ? of([]) : from(validRows).pipe(mergeMap(this.createOrUpdateSetup(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<ISetupExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, SetupSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	private validateRow(ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<ISetupExportData>): SetupImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			row.name = (row.name ?? '').trim();

			if (!row.name) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.errors.missing-name`),
				};
			}
			if (!CUTTING_METHODS.includes(row.cuttingMethod)) {
				errors['cuttingMethod'] = {
					required: this.translator.instant(`${this.translationPrefix}.errors.invalid-cutting-method`, { cuttingMethod: row.cuttingMethod }),
				};
			}
			if (!SORTING_STRATEGIES.includes(row.sortingStrategy)) {
				errors['sortingStrategy'] = {
					required: this.translator.instant(`${this.translationPrefix}.errors.invalid-sorting-strategy`, { sortingStrategy: row.sortingStrategy }),
				};
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: row.name || '',
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			return { row, location };
		};
	}

	private createOrUpdateSetup(ctx: SetupImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: SetupImportCandidate) => {
			const { row, location } = candidate;
			const setup: ISetup = {
				self: null,
				name: row.name,
				description: row.description || '',
				updatedDate: null,
				updatedBy: null,
				minLength: row.minLength ?? null,
				maxLength: row.maxLength ?? null,
				cuttingMethod: `/cutting/setup-cutting-methods/${row.cuttingMethod}`,
				cuttingMethodTranslation: '',
				kerf: row.kerf ?? 0,
				clearance: row.clearance ?? 0,
				rear: row.rear ?? 0,
				// Not editable anywhere in the app (no column, no form field) - a brand-new setup
				// gets the same null the create dialog itself would leave them at, and an update
				// preserves whatever the existing setup already has instead of blanking it out.
				angle: null,
				oversizing: null,
				sortingStrategy: `/cutting/setup-sorting-strategies/${row.sortingStrategy}`,
				sortingStrategyTranslation: '',
			};

			const existingSetup = ctx.setups.find((s) => normalizeName(s.name) === normalizeName(setup.name));

			if (existingSetup) {
				setup.self = existingSetup.self;
				setup.angle = existingSetup.cutting.angle;
				setup.oversizing = existingSetup.cutting.oversizing;
				return this.cuttingService.updateSetup(setup).pipe(
					map(() => ({ entity: setup, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, setup, location, 'update-failed', error);
						return of();
					}),
				);
			}

			return this.cuttingService.createSetup(setup).pipe(
				map(() => ({ entity: setup, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, setup, location, 'create-failed', error);
					return of();
				}),
			);
		};
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], setup: ISetup, location: string | undefined, errorKey: 'update-failed' | 'create-failed', error: unknown) {
		ImportErrors.push({
			Id: setup.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.errors.${errorKey}`, {
				name: setup.name,
				msg: (error as Error)?.message ?? error,
			}),
		});
	}
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
